import React, { useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { Schedule } from '@mui/icons-material';
import { usePermission, Permissions } from '@billda/permissions-ui';
import {
  useOrgAutoSubmitOverview,
  useSetOrgAutoSubmit,
} from '../hooks/useOrgAutoSubmit';
import { shapeOverview } from '../utils/autoSubmitOverview';

const CONSEQUENCES = [
  'It applies from each gang’s and contractor’s next pot. Pots already open keep their current schedule.',
  'Gang leaders and contractors can still turn auto-submit off for themselves, and their own choice always wins.',
  'Pots with nothing approved are skipped, and pots with a negative total are sent back instead of submitted.',
];

/**
 * Auto-submit settings area: org-wide default toggle plus a read-only overview
 * of gang/contractor settings. Turning the default on requires confirmation;
 * turning it off is immediate.
 */
export const AutoSubmitArea: React.FC = () => {
  const canManage = usePermission(Permissions.Organisation.Manage);
  const { data, isLoading } = useOrgAutoSubmitOverview();
  const setOrgAutoSubmit = useSetOrgAutoSubmit();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isEnabled = data?.isEnabled ?? false;
  const overview = shapeOverview(isEnabled, data?.entries ?? []);
  const toggleDisabled = isLoading || !canManage || setOrgAutoSubmit.isPending;

  const handleToggle = (checked: boolean) => {
    if (checked) {
      setConfirmOpen(true);
      return;
    }
    setOrgAutoSubmit.mutate(false);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    setOrgAutoSubmit.mutate(true);
  };

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Schedule sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Auto-submit</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Set the organisation default for auto-submitting pots at the submit
          deadline. It applies to each new pot as it opens; gangs and
          contractors who set their own choice keep it.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <FormControlLabel
          control={
            <Switch
              checked={isEnabled}
              disabled={toggleDisabled}
              onChange={e => handleToggle(e.target.checked)}
              data-testid="org-auto-submit-toggle"
            />
          }
          label={
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Auto-submit all pots
              </Typography>
              <Typography variant="body2" color="text.secondary">
                From the next pot onwards, every pot is scheduled to submit
                automatically when the submit deadline passes. Gang leaders and
                contractors can still turn it off for themselves or submit
                earlier.
              </Typography>
            </Box>
          }
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }} data-testid="auto-submit-overview">
        <Typography variant="h6" sx={{ mb: 2 }}>
          {overview.header}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {overview.emptyMessage ? (
          <Typography variant="body2" color="text.secondary">
            {overview.emptyMessage}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Auto-submit</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overview.rows.map(row => (
                <TableRow key={`${row.typeLabel}-${row.id}`} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Chip label={row.typeLabel} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.stateLabel}
                      size="small"
                      color={row.stateLabel === 'On' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.sourceLabel}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {overview.footerCaption && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 2 }}
          >
            {overview.footerCaption}
          </Typography>
        )}
      </Paper>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        data-testid="org-auto-submit-confirm-dialog"
      >
        <DialogTitle>Turn on auto-submit for all pots?</DialogTitle>
        <DialogContent>
          <List dense disablePadding>
            {CONSEQUENCES.map(text => (
              <ListItem key={text} sx={{ display: 'list-item' }} disableGutters>
                <ListItemText primary={text} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            data-testid="org-auto-submit-confirm-button"
          >
            Turn on auto-submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
